import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import {
  Target, DollarSign, Eye, Clock, Trophy, X, Plus, Search, Filter,
  ChevronRight, Phone, Mail, MapPin, MoreVertical, Edit, Trash2,
  Send, MessageSquare, Calendar, AlertTriangle, CheckCircle, RefreshCw,
  TrendingUp, Users, FileText, ArrowRight, ExternalLink, Building2
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const STAGES = [
  { id: 'all', label: 'All', icon: Target, color: 'slate' },
  { id: 'estimating', label: 'Estimating', icon: FileText, color: 'slate' },
  { id: 'proposed', label: 'Proposed', icon: Send, color: 'blue' },
  { id: 'viewed', label: 'Viewed', icon: Eye, color: 'purple' },
  { id: 'pending', label: 'Pending', icon: Clock, color: 'amber' },
  { id: 'won', label: 'Won', icon: Trophy, color: 'emerald' },
  { id: 'lost', label: 'Lost', icon: X, color: 'red' }
];

const PENDING_REASONS = [
  { value: 'waiting_on_bank', label: 'Waiting on Bank' },
  { value: 'waiting_on_financing', label: 'Waiting on Financing' },
  { value: 'waiting_on_spouse', label: 'Waiting on Spouse/Partner' },
  { value: 'waiting_on_hoa', label: 'Waiting on HOA' },
  { value: 'waiting_on_permit', label: 'Waiting on Permit' },
  { value: 'waiting_on_insurance', label: 'Waiting on Insurance' },
  { value: 'waiting_on_budget', label: 'Waiting on Budget' },
  { value: 'waiting_on_schedule', label: 'Waiting on Schedule' },
  { value: 'other', label: 'Other' }
];

const LOST_REASONS = [
  { value: 'price', label: 'Price too high' },
  { value: 'timing', label: 'Bad timing / not ready' },
  { value: 'competitor', label: 'Went with competitor' },
  { value: 'no_response', label: 'Client went dark (no response)' },
  { value: 'changed_mind', label: 'Client changed mind' },
  { value: 'other', label: 'Other' }
];

const ACTIVITY_TYPES = [
  { value: 'note', label: 'Note', icon: '▸' },
  { value: 'call', label: 'Call', icon: '◆' },
  { value: 'email', label: 'Email', icon: '◇' },
  { value: 'site_visit', label: 'Site Visit', icon: '◈' }
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const SalesPipelinePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [actionRequired, setActionRequired] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════════════════════════════════════════

  const loadData = useCallback(async () => {
    try {
      const [oppsRes, statsRes, actionRes] = await Promise.all([
        API.get(`/opportunities${activeStage !== 'all' ? `?stage=${activeStage}` : ''}${searchQuery ? `&search=${searchQuery}` : ''}`),
        API.get('/opportunities/stats'),
        API.get('/opportunities/action-required')
      ]);
      setOpportunities(oppsRes.data);
      setStats(statsRes.data);
      setActionRequired(actionRes.data);
    } catch (error) {
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  }, [activeStage, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleSendProposal = async (opp) => {
    if (!opp.client?.email) {
      toast.error('Client email required to send proposal');
      return;
    }
    
    try {
      const response = await API.post(`/opportunities/${opp.opportunity_id}/send-proposal`, {
        send_email: true,
        send_sms: false
      });
      toast.success('Proposal sent!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send proposal');
    }
  };

  const handleMarkWon = async (opp) => {
    try {
      await API.post(`/opportunities/${opp.opportunity_id}/mark-won`, {});
      toast.success('🎉 Congratulations! Opportunity marked as Won!');
      loadData();
    } catch (error) {
      toast.error('Failed to mark as won');
    }
  };

  const handleMoveToPending = async (formData) => {
    try {
      await API.post(`/opportunities/${selectedOpp.opportunity_id}/move-pending`, formData);
      toast.success('Moved to Pending');
      setShowPendingModal(false);
      setSelectedOpp(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move to pending');
    }
  };

  const handleMarkLost = async (formData) => {
    try {
      await API.post(`/opportunities/${selectedOpp.opportunity_id}/mark-lost`, formData);
      toast.success('Marked as Lost');
      setShowLostModal(false);
      setSelectedOpp(null);
      loadData();
    } catch (error) {
      toast.error('Failed to mark as lost');
    }
  };

  const handleAddNote = async (formData) => {
    try {
      await API.post(`/opportunities/${selectedOpp.opportunity_id}/activities`, formData);
      toast.success('Note added');
      setShowNoteModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleConvertToProject = async (formData) => {
    try {
      const response = await API.post(`/opportunities/${selectedOpp.opportunity_id}/convert-to-project`, formData);
      toast.success('Project created!');
      setShowConvertModal(false);
      navigate(`/projects/${response.data.project_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleCreateOpportunity = async (formData) => {
    try {
      const response = await API.post('/opportunities', formData);
      toast.success('Opportunity created');
      setShowNewModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create opportunity');
    }
  };

  const handleReactivate = async (opp) => {
    try {
      await API.post(`/opportunities/${opp.opportunity_id}/reactivate`);
      toast.success('Opportunity reactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to reactivate');
    }
  };

  const handleDelete = async (opp) => {
    if (!window.confirm('Delete this opportunity?')) return;
    try {
      await API.delete(`/opportunities/${opp.opportunity_id}`);
      toast.success('Opportunity deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  const getStageColor = (stage) => {
    const colors = {
      estimating: 'bg-slate-500',
      proposed: 'bg-blue-500',
      viewed: 'bg-purple-500',
      pending: 'bg-amber-500',
      won: 'bg-emerald-500',
      lost: 'bg-red-500'
    };
    return colors[stage] || 'bg-slate-500';
  };

  const getStageActions = (opp) => {
    switch (opp.stage) {
      case 'estimating':
        return (
          <>
            <button onClick={() => handleSendProposal(opp)} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500">
              Send Proposal
            </button>
            <button onClick={() => navigate(`/estimating?opp=${opp.opportunity_id}`)} className="px-3 py-1.5 bg-slate-700 text-white text-xs rounded hover:bg-slate-600">
              Edit
            </button>
            <button onClick={() => { setSelectedOpp(opp); setShowLostModal(true); }} className="px-3 py-1.5 bg-red-600/20 text-red-400 text-xs rounded hover:bg-red-600/30">
              Lost
            </button>
          </>
        );
      case 'proposed':
        return (
          <>
            <button onClick={() => { setSelectedOpp(opp); setShowNoteModal(true); }} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500">
              Follow Up ✓
            </button>
            <button onClick={() => handleSendProposal(opp)} className="px-3 py-1.5 bg-slate-700 text-white text-xs rounded hover:bg-slate-600">
              Resend
            </button>
            <button onClick={() => { setSelectedOpp(opp); setShowNoteModal(true); }} className="px-3 py-1.5 bg-slate-700 text-white text-xs rounded hover:bg-slate-600">
              Note
            </button>
          </>
        );
      case 'viewed':
        return (
          <>
            <button onClick={() => { setSelectedOpp(opp); setShowNoteModal(true); }} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500">
              Follow Up ✓
            </button>
            <button onClick={() => { setSelectedOpp(opp); setShowPendingModal(true); }} className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded hover:bg-amber-500">
              Pending
            </button>
            <button onClick={() => handleMarkWon(opp)} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500">
              Won
            </button>
          </>
        );
      case 'pending':
        return (
          <>
            <button onClick={() => { setSelectedOpp(opp); setShowNoteModal(true); }} className="px-3 py-1.5 bg-slate-700 text-white text-xs rounded hover:bg-slate-600">
              Add Note
            </button>
            <button onClick={() => handleMarkWon(opp)} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500">
              Won
            </button>
            <button onClick={() => { setSelectedOpp(opp); setShowLostModal(true); }} className="px-3 py-1.5 bg-red-600/20 text-red-400 text-xs rounded hover:bg-red-600/30">
              Lost
            </button>
          </>
        );
      case 'won':
        return (
          <>
            {!opp.won_details?.converted_to_project ? (
              <button onClick={() => { setSelectedOpp(opp); setShowConvertModal(true); }} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500">
                Convert to Project
              </button>
            ) : (
              <button onClick={() => navigate(`/projects/${opp.won_details.project_id}`)} className="px-3 py-1.5 bg-slate-700 text-white text-xs rounded hover:bg-slate-600">
                View Project
              </button>
            )}
          </>
        );
      case 'lost':
        return (
          <>
            <button onClick={() => handleReactivate(opp)} className="px-3 py-1.5 bg-slate-700 text-white text-xs rounded hover:bg-slate-600">
              Reactivate
            </button>
            <button onClick={() => { setSelectedOpp(opp); setShowDetailModal(true); }} className="px-3 py-1.5 bg-slate-700 text-white text-xs rounded hover:bg-slate-600">
              Details
            </button>
          </>
        );
      default:
        return null;
    }
  };

  const formatCurrency = (val) => `$${(val || 0).toLocaleString()}`;
  
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1117] text-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Target className="w-7 h-7 text-emerald-500" />
            SALES PIPELINE
          </h1>
          <p className="text-gray-400 text-sm">Track opportunities from estimate to close</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500"
        >
          <Plus className="w-4 h-4" />
          New Opportunity
        </button>
      </div>

      {/* Stage Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {STAGES.map((stage) => {
          const stageStats = stage.id === 'all' 
            ? { count: opportunities.length, value: opportunities.reduce((s, o) => s + (o.value || 0), 0) }
            : stats?.by_stage?.[stage.id] || { count: 0, value: 0 };
          
          return (
            <button
              key={stage.id}
              onClick={() => setActiveStage(stage.id)}
              className={`flex flex-col items-center px-4 py-2 rounded-lg border transition-colors min-w-[100px] ${
                activeStage === stage.id
                  ? 'bg-emerald-600 border-emerald-500 text-white'
                  : 'bg-[#161b22] border-[#30363d] text-gray-400 hover:border-gray-500'
              }`}
            >
              <span className="text-xs font-medium uppercase">{stage.label}</span>
              <span className="text-lg font-bold">{stageStats.count}</span>
              <span className="text-xs opacity-70">{formatCurrency(stageStats.value)}</span>
            </button>
          );
        })}
      </div>

      {/* Tactical Intelligence */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#161b22] rounded-xl p-4 border border-[#30363d]">
          <p className="text-gray-400 text-xs uppercase">Win Rate</p>
          <p className="text-2xl font-bold text-emerald-400">{stats?.win_rate || 0}%</p>
          <p className="text-gray-500 text-xs">closed opportunities</p>
        </div>
        <div className="bg-[#161b22] rounded-xl p-4 border border-[#30363d]">
          <p className="text-gray-400 text-xs uppercase">Active Pipeline</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(stats?.active_pipeline_value)}</p>
          <p className="text-gray-500 text-xs">{stats?.active_pipeline_count || 0} opportunities</p>
        </div>
        <div className="bg-[#161b22] rounded-xl p-4 border border-[#30363d]">
          <p className="text-gray-400 text-xs uppercase">Won This Month</p>
          <p className="text-2xl font-bold text-emerald-400">{formatCurrency(stats?.won_this_month?.value)}</p>
          <p className="text-gray-500 text-xs">{stats?.won_this_month?.count || 0} projects</p>
        </div>
        <div className="bg-[#161b22] rounded-xl p-4 border border-[#30363d]">
          <p className="text-gray-400 text-xs uppercase">Oldest Pending</p>
          <p className="text-2xl font-bold text-amber-400">{stats?.oldest_pending_days || 0} days</p>
          <p className="text-gray-500 text-xs truncate">{stats?.oldest_pending_title || 'None'}</p>
        </div>
      </div>

      {/* Action Required */}
      {actionRequired.length > 0 && (
        <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="text-white font-semibold">ACTION REQUIRED ({actionRequired.length})</h3>
          </div>
          <div className="space-y-2">
            {actionRequired.slice(0, 3).map((item, i) => (
              <div key={i} className="flex items-center justify-between bg-[#161b22] rounded-lg p-3">
                <div>
                  <p className="text-white font-medium">{item.title}</p>
                  <p className="text-gray-400 text-sm">{item.client_name} • {item.message}</p>
                </div>
                <button
                  onClick={() => {
                    const opp = opportunities.find(o => o.opportunity_id === item.opportunity_id);
                    if (opp) { setSelectedOpp(opp); setShowNoteModal(true); }
                  }}
                  className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-500"
                >
                  Take Action
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search opportunities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#161b22] border border-[#30363d] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      {/* Opportunities by Stage */}
      {activeStage === 'all' ? (
        // Show all stages
        STAGES.filter(s => s.id !== 'all').map((stage) => {
          const stageOpps = opportunities.filter(o => o.stage === stage.id);
          if (stageOpps.length === 0) return null;
          
          const stageValue = stageOpps.reduce((s, o) => s + (o.value || 0), 0);
          
          return (
            <div key={stage.id} className="mb-8">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${getStageColor(stage.id)}`}></span>
                {stage.label.toUpperCase()} ({stageOpps.length}) - {formatCurrency(stageValue)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stageOpps.map((opp) => (
                  <OpportunityCard key={opp.opportunity_id} opp={opp} onAction={getStageActions} formatCurrency={formatCurrency} formatTimeAgo={formatTimeAgo} onClick={() => { setSelectedOpp(opp); setShowDetailModal(true); }} />
                ))}
              </div>
            </div>
          );
        })
      ) : (
        // Show single stage
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {opportunities.map((opp) => (
            <OpportunityCard key={opp.opportunity_id} opp={opp} onAction={getStageActions} formatCurrency={formatCurrency} formatTimeAgo={formatTimeAgo} onClick={() => { setSelectedOpp(opp); setShowDetailModal(true); }} />
          ))}
        </div>
      )}

      {opportunities.length === 0 && (
        <div className="text-center py-12">
          <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No opportunities found</p>
          <button onClick={() => setShowNewModal(true)} className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500">
            Create Your First Opportunity
          </button>
        </div>
      )}

      {/* Modals */}
      {showNewModal && <NewOpportunityModal onClose={() => setShowNewModal(false)} onCreate={handleCreateOpportunity} />}
      {showDetailModal && selectedOpp && <OpportunityDetailModal opp={selectedOpp} onClose={() => { setShowDetailModal(false); setSelectedOpp(null); }} onRefresh={loadData} />}
      {showPendingModal && selectedOpp && <MoveToPendingModal opp={selectedOpp} onClose={() => { setShowPendingModal(false); setSelectedOpp(null); }} onSubmit={handleMoveToPending} />}
      {showLostModal && selectedOpp && <MarkLostModal opp={selectedOpp} onClose={() => { setShowLostModal(false); setSelectedOpp(null); }} onSubmit={handleMarkLost} />}
      {showNoteModal && selectedOpp && <AddNoteModal opp={selectedOpp} onClose={() => { setShowNoteModal(false); setSelectedOpp(null); }} onSubmit={handleAddNote} />}
      {showConvertModal && selectedOpp && <ConvertToProjectModal opp={selectedOpp} onClose={() => { setShowConvertModal(false); setSelectedOpp(null); }} onSubmit={handleConvertToProject} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// OPPORTUNITY CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const OpportunityCard = ({ opp, onAction, formatCurrency, formatTimeAgo, onClick }) => {
  const getStageInfo = () => {
    switch (opp.stage) {
      case 'estimating':
        return { text: `Created ${formatTimeAgo(opp.created_at)}`, subtext: opp.activities?.[0]?.content };
      case 'proposed':
        const daysSent = opp.proposal?.sent_at ? Math.floor((new Date() - new Date(opp.proposal.sent_at)) / (1000 * 60 * 60 * 24)) : 0;
        return { text: `Sent ${formatTimeAgo(opp.proposal?.sent_at)} • ${daysSent} days waiting`, subtext: `Sent to ${opp.proposal?.sent_to}` };
      case 'viewed':
        return { text: `Viewed ${formatTimeAgo(opp.proposal?.first_viewed_at)}`, subtext: `${opp.proposal?.view_count || 0} total views` };
      case 'pending':
        const reason = PENDING_REASONS.find(r => r.value === opp.pending_details?.reason)?.label || opp.pending_details?.reason;
        return { text: reason, subtext: opp.pending_details?.details };
      case 'won':
        return { text: `Won ${formatTimeAgo(opp.won_details?.won_at)}`, subtext: opp.won_details?.converted_to_project ? 'Converted to project' : 'Ready to convert' };
      case 'lost':
        const lostReason = LOST_REASONS.find(r => r.value === opp.lost_details?.reason)?.label || opp.lost_details?.reason;
        return { text: `Lost - ${lostReason}`, subtext: opp.lost_details?.notes };
      default:
        return { text: '', subtext: '' };
    }
  };

  const stageInfo = getStageInfo();
  const hasUrgentReminder = opp.reminders?.some(r => r.status === 'pending');

  return (
    <div className="bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden hover:border-emerald-500/50 transition-colors">
      {/* Header */}
      <div className="p-4 cursor-pointer" onClick={onClick}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {hasUrgentReminder && <AlertTriangle className="w-4 h-4 text-amber-500" />}
              <h3 className="text-white font-medium truncate">{opp.title}</h3>
            </div>
            <p className="text-gray-400 text-sm uppercase">{opp.client?.name || 'No client'}</p>
          </div>
          <p className="text-emerald-400 font-semibold">{formatCurrency(opp.value)}</p>
        </div>
        
        {opp.client?.email && (
          <p className="text-gray-500 text-xs flex items-center gap-1">
            <Mail className="w-3 h-3" />
            {opp.client.email}
            {opp.client.phone && <> • <Phone className="w-3 h-3" /> {opp.client.phone}</>}
          </p>
        )}
      </div>

      {/* Stage Info */}
      <div className="px-4 pb-3 border-t border-[#30363d] pt-3">
        <p className="text-gray-400 text-sm">{stageInfo.text}</p>
        {stageInfo.subtext && (
          <p className="text-gray-500 text-xs mt-1 truncate">▸ {stageInfo.subtext}</p>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 pb-4 flex flex-wrap gap-2">
        {onAction(opp)}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MODAL COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const ModalWrapper = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-[#161b22] rounded-xl border border-[#30363d] w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-4 border-b border-[#30363d]">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

const NewOpportunityModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    value: '',
    client: { name: '', email: '', phone: '' }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate({ ...formData, value: parseFloat(formData.value) || 0 });
  };

  return (
    <ModalWrapper title="NEW OPPORTUNITY" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Title *</label>
          <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" required />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Client Name</label>
          <input type="text" value={formData.client.name} onChange={(e) => setFormData({ ...formData, client: { ...formData.client, name: e.target.value } })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Client Email</label>
            <input type="email" value={formData.client.email} onChange={(e) => setFormData({ ...formData, client: { ...formData.client, email: e.target.value } })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Client Phone</label>
            <input type="tel" value={formData.client.phone} onChange={(e) => setFormData({ ...formData, client: { ...formData.client, phone: e.target.value } })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Value ($)</label>
          <input type="number" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Description</label>
          <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" rows={3} />
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500">Create</button>
        </div>
      </form>
    </ModalWrapper>
  );
};

const MoveToPendingModal = ({ opp, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    reason: '',
    reason_other: '',
    details: '',
    expected_date: '',
    reminder_frequency_days: 7
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.reason || !formData.details) {
      toast.error('Please fill in required fields');
      return;
    }
    onSubmit(formData);
  };

  return (
    <ModalWrapper title="MOVE TO PENDING" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Why is this pending? *</label>
          <div className="space-y-2">
            {PENDING_REASONS.map((reason) => (
              <label key={reason.value} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="reason" value={reason.value} checked={formData.reason === reason.value} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} className="text-emerald-500" />
                <span className="text-gray-300">{reason.label}</span>
              </label>
            ))}
          </div>
        </div>
        {formData.reason === 'other' && (
          <div>
            <label className="block text-sm text-gray-400 mb-1">Specify reason</label>
            <input type="text" value={formData.reason_other} onChange={(e) => setFormData({ ...formData, reason_other: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        )}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Details (required) *</label>
          <textarea value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" rows={3} required placeholder="e.g., Client applied for Chase home equity loan on Jan 10..." />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Expected Resolution Date</label>
            <input type="date" value={formData.expected_date} onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Reminder Frequency</label>
            <select value={formData.reminder_frequency_days} onChange={(e) => setFormData({ ...formData, reminder_frequency_days: parseInt(e.target.value) })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
              <option value={3}>Every 3 days</option>
              <option value={7}>Every 7 days</option>
              <option value={14}>Every 14 days</option>
              <option value={30}>Every 30 days</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500">Move to Pending</button>
        </div>
      </form>
    </ModalWrapper>
  );
};

const MarkLostModal = ({ opp, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    reason: '',
    reason_other: '',
    competitor_name: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.reason) {
      toast.error('Please select a reason');
      return;
    }
    onSubmit(formData);
  };

  return (
    <ModalWrapper title="MARK AS LOST" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Why was this lost? *</label>
          <div className="space-y-2">
            {LOST_REASONS.map((reason) => (
              <label key={reason.value} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="reason" value={reason.value} checked={formData.reason === reason.value} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} className="text-emerald-500" />
                <span className="text-gray-300">{reason.label}</span>
              </label>
            ))}
          </div>
        </div>
        {formData.reason === 'other' && (
          <div>
            <label className="block text-sm text-gray-400 mb-1">Specify reason</label>
            <input type="text" value={formData.reason_other} onChange={(e) => setFormData({ ...formData, reason_other: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        )}
        {formData.reason === 'competitor' && (
          <div>
            <label className="block text-sm text-gray-400 mb-1">Competitor Name</label>
            <input type="text" value={formData.competitor_name} onChange={(e) => setFormData({ ...formData, competitor_name: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        )}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Notes</label>
          <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" rows={3} placeholder="Any additional details..." />
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500">Mark Lost</button>
        </div>
      </form>
    </ModalWrapper>
  );
};

const AddNoteModal = ({ opp, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    type: 'note',
    content: '',
    set_reminder: false,
    reminder_date: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.content) {
      toast.error('Please enter a note');
      return;
    }
    onSubmit(formData);
  };

  return (
    <ModalWrapper title="ADD NOTE" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Note Type</label>
          <div className="flex gap-4">
            {ACTIVITY_TYPES.map((type) => (
              <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="type" value={type.value} checked={formData.type === type.value} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="text-emerald-500" />
                <span className="text-gray-300">{type.icon} {type.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" rows={4} placeholder="Enter your note..." required />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="setReminder" checked={formData.set_reminder} onChange={(e) => setFormData({ ...formData, set_reminder: e.target.checked })} className="text-emerald-500" />
          <label htmlFor="setReminder" className="text-gray-300 cursor-pointer">Set follow-up reminder</label>
        </div>
        {formData.set_reminder && (
          <div>
            <label className="block text-sm text-gray-400 mb-1">Reminder Date</label>
            <input type="date" value={formData.reminder_date} onChange={(e) => setFormData({ ...formData, reminder_date: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        )}
        <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500">Save Note</button>
        </div>
      </form>
    </ModalWrapper>
  );
};

const ConvertToProjectModal = ({ opp, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    copy_line_items: true,
    create_schedule: true,
    send_notification: false,
    estimated_start_date: '',
    estimated_duration_weeks: 4
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <ModalWrapper title="CONVERT TO ACTIVE PROJECT" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-[#0d1117] rounded-lg p-4">
          <p className="text-white font-medium">{opp.title}</p>
          <p className="text-gray-400 text-sm">Client: {opp.client?.name || 'N/A'}</p>
          <p className="text-emerald-400 font-semibold">${(opp.value || 0).toLocaleString()}</p>
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={formData.copy_line_items} onChange={(e) => setFormData({ ...formData, copy_line_items: e.target.checked })} className="text-emerald-500" />
            <span className="text-gray-300">Copy estimate line items to project budget</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={formData.create_schedule} onChange={(e) => setFormData({ ...formData, create_schedule: e.target.checked })} className="text-emerald-500" />
            <span className="text-gray-300">Create initial project schedule</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={formData.send_notification} onChange={(e) => setFormData({ ...formData, send_notification: e.target.checked })} className="text-emerald-500" />
            <span className="text-gray-300">Send "Project Started" notification to client</span>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Estimated Start Date</label>
            <input type="date" value={formData.estimated_start_date} onChange={(e) => setFormData({ ...formData, estimated_start_date: e.target.value })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Estimated Duration</label>
            <select value={formData.estimated_duration_weeks} onChange={(e) => setFormData({ ...formData, estimated_duration_weeks: parseInt(e.target.value) })} className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
              <option value={1}>1 week</option>
              <option value={2}>2 weeks</option>
              <option value={4}>4 weeks</option>
              <option value={6}>6 weeks</option>
              <option value={8}>8 weeks</option>
              <option value={12}>12 weeks</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500">Create Project →</button>
        </div>
      </form>
    </ModalWrapper>
  );
};

const OpportunityDetailModal = ({ opp, onClose, onRefresh }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadActivities = async () => {
      try {
        const response = await API.get(`/opportunities/${opp.opportunity_id}/activities`);
        setActivities(response.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadActivities();
  }, [opp.opportunity_id]);

  const getActivityIcon = (type) => {
    const icons = { note: '▸', call: '◆', email: '◇', site_visit: '◈', stage_change: '→', proposal_sent: '📧', proposal_viewed: '👁', reminder_completed: '✓', system: '⚙' };
    return icons[type] || '•';
  };

  return (
    <ModalWrapper title={opp.title} onClose={onClose}>
      <div className="space-y-6">
        {/* Client Info */}
        <div className="bg-[#0d1117] rounded-lg p-4">
          <h4 className="text-gray-400 text-sm mb-2">CLIENT</h4>
          <p className="text-white font-medium">{opp.client?.name || 'No client'}</p>
          {opp.client?.email && <p className="text-gray-400 text-sm flex items-center gap-1"><Mail className="w-3 h-3" /> {opp.client.email}</p>}
          {opp.client?.phone && <p className="text-gray-400 text-sm flex items-center gap-1"><Phone className="w-3 h-3" /> {opp.client.phone}</p>}
        </div>

        {/* Value */}
        <div className="flex items-center justify-between">
          <span className="text-gray-400">Value</span>
          <span className="text-2xl font-bold text-emerald-400">${(opp.value || 0).toLocaleString()}</span>
        </div>

        {/* Stage */}
        <div className="flex items-center justify-between">
          <span className="text-gray-400">Stage</span>
          <span className="px-3 py-1 rounded-full text-sm font-medium capitalize" style={{ backgroundColor: opp.stage === 'won' ? '#059669' : opp.stage === 'lost' ? '#dc2626' : opp.stage === 'pending' ? '#d97706' : '#475569' }}>
            {opp.stage}
          </span>
        </div>

        {/* Pending Details */}
        {opp.stage === 'pending' && opp.pending_details && (
          <div className="bg-amber-900/20 border border-amber-800/50 rounded-lg p-4">
            <h4 className="text-amber-400 text-sm mb-2">PENDING REASON</h4>
            <p className="text-white">{PENDING_REASONS.find(r => r.value === opp.pending_details.reason)?.label}</p>
            <p className="text-gray-400 text-sm mt-1">{opp.pending_details.details}</p>
            {opp.pending_details.expected_date && <p className="text-gray-500 text-xs mt-2">Expected: {opp.pending_details.expected_date}</p>}
          </div>
        )}

        {/* Activity Log */}
        <div>
          <h4 className="text-gray-400 text-sm mb-3">ACTIVITY LOG</h4>
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500 mx-auto"></div>
            </div>
          ) : activities.length === 0 ? (
            <p className="text-gray-500 text-sm">No activity yet</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {activities.map((act, i) => (
                <div key={i} className="bg-[#0d1117] rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <span className="text-emerald-500">{getActivityIcon(act.type)}</span>
                    <div className="flex-1">
                      <p className="text-gray-300 text-sm">{act.content}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {new Date(act.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export default SalesPipelinePage;
